import { type TypedDocumentNode, useApolloClient } from '@apollo/client'
import {
  DataCollectionPaginatedFetchOptions,
  DataCollectionSource,
  DataCollectionSourceDefinition,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  NavigationFiltersState,
  PaginatedResponse,
  RecordType,
  SortingsDefinition,
  SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'

import { DEFAULT_PAGE_SIZE, MapVariablesParams, PaginationVars } from './useGraphqlDataSource'

export type GraphqlDataCollectionSourceDefinition<
  R extends RecordType = RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Variables extends PaginationVars = PaginationVars,
  Document extends TypedDocumentNode<ResultOf<Document>, Variables> = TypedDocumentNode,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<R> = GroupingDefinition<R>,
  Summaries extends SummariesDefinition = SummariesDefinition
> = Omit<
  DataCollectionSourceDefinition<
    R,
    Filters,
    Sortings,
    Summaries,
    ItemActionsDefinition<R>,
    Navigation,
    Grouping
  >,
  'dataAdapter'
> & {
  document: Document
  fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only'
  paginationType?: 'pages' | 'infinite-scroll'
  perPage?: number
  mapVariables: ({
    filters,
    sortings,
    search,
    navigationFilters,
    pagination,
  }: MapVariablesParams<Filters> & {
    navigationFilters?: NavigationFiltersState<Navigation>
  }) => Omit<VariablesOf<Document>, 'page' | 'perPage' | 'cursor'>
  mapData: (data: ResultOf<Document>) => Array<R>
  mapSummaries?: (data: ResultOf<Document>) => R | undefined
  mapPagination: (data: ResultOf<Document>) => {
    total: number
    cursor?: string
    hasNextPage?: boolean
  }
}

export const useGraphqlDataCollectionSource = <
  Record extends RecordType,
  Filters extends FiltersDefinition,
  Sortings extends SortingsDefinition,
  Summaries extends SummariesDefinition,
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>>,
  Navigation extends NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<Record>
>(
  {
    mapVariables,
    mapData,
    mapPagination,
    mapSummaries,
    document,
    fetchPolicy = 'cache-and-network',
    paginationType = 'pages',
    perPage = DEFAULT_PAGE_SIZE,
    ...rest
  }: GraphqlDataCollectionSourceDefinition<
    Record,
    Filters,
    Sortings,
    Extract<VariablesOf<Document>, PaginationVars>,
    Document,
    Navigation,
    Grouping,
    Summaries
  >,
  deps: ReadonlyArray<unknown> = []
): DataCollectionSource<
  Record,
  Filters,
  Sortings,
  Summaries,
  ItemActionsDefinition<Record>,
  Navigation,
  Grouping
> => {
  const client = useApolloClient()

  const source = useDataCollectionSource<
    Record,
    Filters,
    Sortings,
    Summaries,
    ItemActionsDefinition<Record>,
    Navigation,
    Grouping
  >(
    {
      ...rest,
      dataAdapter: {
        perPage,
        paginationType,
        fetchData: (options: DataCollectionPaginatedFetchOptions<Filters, Navigation>) => {
          const { filters, sortings, search, navigationFilters } = options
          const pagination =
            'pagination' in options
              ? options.pagination
              : {
                  perPage: DEFAULT_PAGE_SIZE,
                  currentPage: 1,
                }

          const paginationVars: PaginationVars = {
            perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
            __notExcludePaginationKeysFromKeyArgs: true,
            ...(paginationType === 'pages'
              ? { page: pagination?.currentPage || 1, __paginationKind: 'offset' as const }
              : {
                  ...(pagination?.cursor && pagination.cursor !== '0'
                    ? {
                        cursor: pagination.cursor,
                        after: pagination.cursor,
                        __paginationKind: 'cursor' as const,
                        __paginationInfiniteScroll: true,
                      }
                    : {
                        __paginationKind: 'cursor' as const,
                        __paginationInfiniteScroll: true,
                      }),
                }),
          }

          let finalFetchPolicy = fetchPolicy
          if (
            paginationType === 'infinite-scroll' &&
            pagination.cursor &&
            pagination.cursor !== '0'
          ) {
            finalFetchPolicy = 'network-only'
          }

          const observable = client
            .watchQuery({
              query: document,
              fetchPolicy: finalFetchPolicy,
              variables: {
                ...mapVariables({ filters, sortings, search, navigationFilters }),
                ...paginationVars,
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              const mappedData = mapData(data)
              const mappedPagination = mapPagination(data)
              const mappedSummaries = mapSummaries?.(data)

              const baseData = {
                records: mappedData,
                total: mappedPagination.total,
                perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
              }

              const fromCache = loading && !!data

              const paginatedData: PaginatedResponse<Record> =
                paginationType === 'pages'
                  ? {
                      ...baseData,
                      type: 'pages',
                      currentPage: pagination?.currentPage ?? 1,
                      pagesCount: Math.ceil(mappedPagination.total / paginationVars.perPage),
                    }
                  : {
                      ...baseData,
                      records: fromCache ? mappedData.slice(0, paginationVars.perPage) : mappedData,
                      type: 'infinite-scroll',
                      cursor: mappedPagination.cursor ?? null,
                      hasMore: Boolean(mappedPagination.hasNextPage),
                    }

              return {
                loading,
                error,
                data: {
                  ...paginatedData,
                  summaries: mappedSummaries,
                },
              }
            })

          return observable
        },
      },
    },
    deps
  )

  return source
}
